package com.exura.pages.dashboard

import androidx.compose.runtime.*
import com.exura.components.FlexContent
import com.exura.components.buttons.AsyncButton
import com.exura.components.buttons.ButtonOutlineVariant
import com.exura.components.dialogs.YesNoDialog
import com.exura.components.icons.UntiledIcon
import com.exura.components.layouts.NavigableLayoutScope
import com.exura.components.loading.Loader
import com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
import com.exura.components.pageTypography.GeneralListEntry
import com.exura.components.pageTypography.HoverableListEntryVariant
import com.exura.components.pageTypography.PageErrorContent
import com.exura.components.pageTypography.PageMainContent
import com.exura.components.pageTypography.PageNavigationHeaderSection
import com.exura.components.pageTypography.PageOptionsSection
import com.exura.components.pageTypography.PageSecondaryContent
import com.exura.components.pageTypography.UnderwrittenText
import com.exura.features.firebase.domain.model.versioning.Version
import com.exura.features.localization.commonLocalization
import com.exura.features.plandefinition.domain.model.metadata.PlanDefinitionMetadata
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.planLocalization
import com.exura.features.plandefinition.presentation.PlanDefinitionListEntry
import com.exura.navigation.Route
import com.exura.ui.Assets
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.JustifyContent

@Page(Route.DASHBOARD)
@Layout(".components.layouts.NavigableLayout")
@Composable
internal fun NavigableLayoutScope.DashboardPage() {
    val context = rememberPageContext()
    val scope = rememberCoroutineScope()

    val controller = rememberTemplateDashboardController(scope, context)
    val state = controller.uiState

    val onDeletePlan = { plan: Version<BasePlan, PlanDefinitionMetadata> ->
        showDialog {
            YesNoDialog(
                onDismiss = { closeDialog() },
                onConfirm = {
                    scope.launch {
                        plan.firebaseDocId?.let {
                            controller.handleEvent(TemplateDashboardEvent.DeletePlan(it))
                        }
                        closeDialog()
                    }
                }
            )
        }
    }

    Column(modifier = Modifier.fillMaxSize()) {
        PageNavigationHeaderSection(
            planLocalization.current.treatmentPlans,
            isPageWorking = state.isLoading,
            onBack = null,
        )

        when {
            state.isLoading && state.plans.isEmpty() -> Loader()
            state.error != null -> PageErrorContent(errorMessage = state.error)
            else -> TreatmentPlansView(
                modifier = Modifier.weight(1f),
                plans = state.plans,
                onEvent = { scope.launch { controller.handleEvent(it) } },
                onDeletePlan = onDeletePlan,
            )
        }
    }
}

@Composable
private fun TreatmentPlansView(
    modifier: Modifier = Modifier,
    plans: List<Version<BasePlan, PlanDefinitionMetadata>>,
    onEvent: (TemplateDashboardEvent) -> Unit,
    onDeletePlan: (Version<BasePlan, PlanDefinitionMetadata>) -> Unit,
) {
    PageMainContent(modifier) {
        Column(modifier = Modifier.fillMaxSize()) {
            TreatmentPlansOptionsSection(
                onRefresh = { onEvent(TemplateDashboardEvent.RefreshPlans) },
            )
            TreatmentPlansList(
                modifier = Modifier.weight(1f),
                plans = plans,
                onEvent = onEvent,
                onDeletePlan = onDeletePlan,
            )
        }
    }
}

@Composable
private fun TreatmentPlansOptionsSection(
    onRefresh: () -> Unit,
) = PageOptionsSection {
    AsyncButton(
        text = commonLocalization.current.controls.refresh,
        onClick = onRefresh,
        icon = { UntiledIcon(Assets.IconsUntiled.GENERAL_REFRESH) },
        variant = ButtonOutlineVariant,
    )
}

@Composable
private fun TreatmentPlansList(
    modifier: Modifier = Modifier,
    plans: List<Version<BasePlan, PlanDefinitionMetadata>>,
    onEvent: (TemplateDashboardEvent) -> Unit,
    onDeletePlan: (Version<BasePlan, PlanDefinitionMetadata>) -> Unit,
) = PageSecondaryContent(modifier) {
    Column(modifier = Modifier.fillMaxSize()) {
        plans.forEach { plan ->
            TreatmentPlanEntry(
                plan = plan,
                onDeleteClicked = { onDeletePlan(plan) },
                onViewClicked = { plan.firebaseDocId?.let { onEvent(TemplateDashboardEvent.PreviewPlan(it)) } },
                onEditClicked = { plan.firebaseDocId?.let { onEvent(TemplateDashboardEvent.EditPlan(it)) } },
                onProceedClicked = { plan.firebaseDocId?.let { onEvent(TemplateDashboardEvent.TransferPlan(it)) } },
            )

        }
    }
}

@Composable
private fun TreatmentPlanEntry(
    plan: Version<BasePlan, PlanDefinitionMetadata>,
    onDeleteClicked: () -> Unit,
    onViewClicked: () -> Unit,
    onEditClicked: () -> Unit,
    onProceedClicked: () -> Unit,
) = GeneralListEntry(
    modifier = Modifier
        .cursor(Cursor.Default),
    variant = HoverableListEntryVariant,
) {
    Row(
        modifier = Modifier.fillMaxSize(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        FlexContent(
            weight = 8,
            justifyContent = JustifyContent.Start,
        ) {
            PlanDefinitionListEntry(
                version = plan,
                variant = null,
                onClick = null,
            )
        }
        FlexContent(
            weight = 1,
            justifyContent = JustifyContent.Center,
        ) {
            PlanDefinitionEntryOptions(
                onDeleteClicked = onDeleteClicked,
                onViewClicked = onViewClicked,
                onEditClicked = onEditClicked,
                onProceedClicked = onProceedClicked,
            )
        }
    }
}

@Composable
private fun PlanDefinitionEntryOptions(
    onDeleteClicked: () -> Unit,
    onViewClicked: () -> Unit,
    onEditClicked: () -> Unit,
    onProceedClicked: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.Bottom,
        horizontalArrangement = Arrangement.SpaceEvenly,
    ) {
        PlanActionOption(
            text = commonLocalization.current.crud.delete,
            iconSrc = Assets.IconsUntiled.DELETE_PLAN,
            onClick = onDeleteClicked,
        )

        PlanActionOption(
            text = commonLocalization.current.crud.preview,
            iconSrc = Assets.IconsUntiled.PREVIEW_PLAN,
            onClick = onViewClicked,
        )

        PlanActionOption(
            text = commonLocalization.current.crud.edit,
            iconSrc = Assets.IconsUntiled.EDIT,
            onClick = onEditClicked,
        )

        PlanActionOption(
            text = commonLocalization.current.crud.send,
            iconSrc = Assets.IconsUntiled.PROCEED,
            onClick = onProceedClicked,
        )
    }
}

@Composable
private fun PlanActionOption(
    text: String,
    iconSrc: String,
    onClick: () -> Unit,
) {
    var isHoveredIcon by remember { mutableStateOf(false) }

    UnderwrittenText(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .onMouseEnter { isHoveredIcon = true }
            .onMouseLeave { isHoveredIcon = false },
        topVariant = ExuraBodyHeaderTextVariant,
        underwrite = { if (isHoveredIcon) SpanText(text) },
    ) {
        UntiledIcon(iconSrc)
    }

}
